<template>
    <service-card
        class="accommodation-service"
        :service="formattedService"
        icon="mdi-hotel"
        :is-first="isFirst"
        :service-tips="serviceTips"
        :order="order"
        :variants-available="variantsAvailable"
        @show-info="showInfo"
        @modify-service="$emit('modify-service', $event)"
        @minimized-change="minimizedChange"
    >
        <template v-slot:checkbox>
            <slot name="checkbox" />
        </template>
        <template v-slot:icons>
            <slot name="icons" />
        </template>
        <template v-slot:orderInfo>
            <order-info-base
                :service="service"
                :service-check-in="serviceCheckIn"
                :service-check-out="serviceCheckOut"
                :is-package="isPackage"
                :service-image="mainImage"
                :order="order"
                :info="info"
                :order-end-date="orderEndDate"
                :not-itinerary-start="notItineraryStart"
                @cancel-service="$emit('cancel-service', $event)"
                @modify-service="$emit('modify-service', $event)"
            >
                <v-skeleton-loader v-if="isInfoLoading" type="paragraph" />
                <template v-if="info && info.address" v-slot:additionalInfo>
                    <hotel-address-label
                        :is-show-label="true"
                        :address="hotelAddress"
                        :hide-show-on-map-button="!info.latitude && !info.longitude"
                        @click="mapModal = true"
                    />
                </template>
                <div v-if="hotelConfirmationNumber" class="order-info-base-info">
                    <span class="order-info-base-info__icon">
                        <v-icon start size="small">mdi-check-underline</v-icon>
                        <span class="text-caption text-capitalize">{{ $t('hotel_confirmation') }} #</span>
                    </span>
                    <span class="order-info-base-info__text">
                        {{ hotelConfirmationNumber }}
                    </span>
                </div>
                <div class="order-info-base-info">
                    <span class="order-info-base-info__icon">
                        <v-icon start size="x-small">mdi-weather-night</v-icon>
                        <span class="text-body-2 text-capitalize">{{ $t('night', nights) }}</span>
                    </span>
                    <span class="order-info-base-info__text">
                        {{ `${nights} ${$t('night', nights)}` }}
                    </span>
                </div>
                <div
                    v-if="service.status === 'Quote' && !isReadOnly"
                    class="accommodation-service__details d-flex align-center"
                >
                    <v-icon start size="small" color="primary" @click="fillAccommodationBookingDataModal = true">
                        mdi-pencil
                    </v-icon>
                    <span
                        class="text-caption cursor-pointer text-primary"
                        @click="fillAccommodationBookingDataModal = true"
                    >
                        {{ $t('enter_details') }}
                    </span>
                    <fill-accommodation-booking-data-modal
                        v-model="fillAccommodationBookingDataModal"
                        :service="service"
                        :order="order"
                    />
                </div>
                <template v-slot:travelers="{emptyAdditionalFields}">
                    <v-row v-for="room in rooms" :key="room.roomId" no-gutters>
                        <v-col class="accommodation-service__travelers-col mt-3">
                            <div class="accommodation-service__travelers-title d-flex text-subtitle-1">
                                <v-progress-circular
                                    v-if="isInfoLoading"
                                    indeterminate
                                    size="24"
                                    class="me-2"
                                    color="grey"
                                />
                                <v-icon v-else start size="small" @click="roomInfo(room)">
                                    mdi-information-outline
                                </v-icon>
                                <!-- ZSH: append " - Zero/Sustainable Room" to the room name for
                                     Zero/Sustainable levels; drop the rate-plan "(...)" from this
                                     line; add a zsh-green leaf marker at the end of the line. -->
                                <div class="accommodation-service__travelers-title-main d-flex align-center">
                                    <v-icon start size="x-small">mdi-bed-empty</v-icon>
                                    <p
                                        class="accommodation-service__travelers-title-txt text-subtitle-2 font-weight-bold"
                                    >
                                        {{ room.roomTypeName
                                        }}<template v-if="room.sustainabilityLevel === 'ZERO'">
                                            - {{ $t('zero_room') }}</template
                                        ><template v-else-if="room.sustainabilityLevel === 'SUSTAINABLE'">
                                            - {{ $t('sustainable_room') }}</template
                                        >
                                    </p>
                                </div>
                                <hotel-bed-type-label
                                    v-if="room.bedTypes && room.bedTypes.length"
                                    :bed-types="room.bedTypes"
                                    class="ms-3"
                                />
                                <v-icon
                                    v-if="
                                        room.sustainabilityLevel === 'ZERO' ||
                                        room.sustainabilityLevel === 'SUSTAINABLE'
                                    "
                                    class="ms-2"
                                    color="#237E71"
                                    >mdi-leaf</v-icon
                                >
                            </div>
                            <!-- UI-24-1: sustainability room note (Zero: added amenities; Sustainable: omitted) + (i) modal -->
                            <RoomSustainabilityNote
                                :room="room"
                                :service="service"
                                :nights="nights"
                                class="mt-3 mb-3"
                            />
                            <v-divider class="my-3" />
                            <travelers
                                :service="service"
                                :travelers="roomTravelers(room)"
                                :product="service.serviceType"
                                :details="service.serviceDetails"
                                :order-end-date="orderEndDate"
                                :empty-additional-fields="emptyAdditionalFields"
                            >
                                <template v-slot:mealType="{traveler}">
                                    <v-col cols="12" md="2">
                                        <small class="d-block traveler-label text-body-2">
                                            {{ $t('filterTitles.mealTypes') }}
                                        </small>
                                        <div class="d-flex align-center">
                                            <v-progress-circular
                                                v-if="isInfoLoading"
                                                indeterminate
                                                size="24"
                                                class="me-2"
                                                color="grey"
                                            />
                                            <v-icon
                                                v-else
                                                start
                                                :loading="isInfoLoading"
                                                size="small"
                                                @click="mealTypeInfo(room)"
                                            >
                                                {{
                                                    room.otaMealTypeCode !== '14'
                                                        ? ' mdi-silverware-fork-knife'
                                                        : 'mdi-information-outline'
                                                }}
                                            </v-icon>
                                            <span class="text-uppercase text-black font-weight-bold">
                                                {{ traveler.mealType ? traveler.mealType : room.mealTypeName }}
                                            </span>
                                        </div>
                                    </v-col>
                                </template>
                            </travelers>
                        </v-col>
                    </v-row>
                    <ModalWrapper v-if="roomData" v-model="roomModal" width="600" :scrim="false">
                        <template v-slot:content>
                            <div class="mb-2 text-h3">
                                {{ service.serviceName }}
                            </div>
                            <div v-if="roomData.roomTypeName" class="mb-1">
                                <span class="font-weight-medium">{{ $t('roomGroupBy.name') }}</span>
                                : {{ roomData.roomTypeName }}
                            </div>
                            <div v-if="roomData.mealTypeName" class="mb-1">
                                <span class="font-weight-medium">{{ $t('roomGroupBy.name') }}</span>
                                : {{ roomData.mealTypeName }}
                            </div>
                        </template>
                    </ModalWrapper>
                </template>
            </order-info-base>
        </template>
        <template v-slot:serviceInfo>
            <service-info-dialog
                ref="serviceInfoDialog"
                v-model="modal"
                :service="formattedService"
                :info="infoData"
                :image="mainImage"
                :is-info-loading="isInfoLoading"
                enable-custom-product-info
                @close-modal="closeModal"
            >
                <v-icon class="v-card-close" @click="modal = false">mdi-close</v-icon>
                <template v-if="info" v-slot:serviceInfo>
                    <div class="d-flex align-center">
                        <v-icon start size="small" color="info">mdi-map-marker</v-icon>
                        <small>{{ hotelAddress }}</small>
                    </div>
                    <div
                        v-if="info.email && showContacts && isShowHotelEmailOnInfo"
                        class="d-flex align-center text-caption"
                    >
                        <v-icon start size="small">mdi-at</v-icon>
                        {{ info.email }}
                    </div>
                    <div v-if="info.url" class="d-flex align-center text-caption">
                        <v-icon start size="small">mdi-web</v-icon>
                        <a :href="info.url" target="_blank" class="text-caption">
                            {{ info.url }}
                        </a>
                    </div>
                    <v-row no-gutters>
                        <v-col v-if="info.phone && showContacts" :cols="12" md="12">
                            <div class="d-flex align-center text-caption">
                                <v-icon start size="small">mdi-phone</v-icon>
                                {{ info.phone }}
                            </div>
                        </v-col>
                    </v-row>
                    <v-row no-gutters>
                        <v-col v-if="info.fax && showContacts" :cols="12" md="12">
                            <div class="d-flex align-center text-caption">
                                <v-icon start size="small">mdi-fax</v-icon>
                                {{ info.fax }}
                            </div>
                        </v-col>
                    </v-row>
                </template>
                <!-- TODO: rewrite descriptions like in HotelPage (hotel-info-descriptions) -->
                <template v-if="info" v-slot:extraInfo>
                    <v-col :cols="12" md="12" class="mt-5">
                        <div v-if="info.descriptions.description.find(el => el.type === 'short')" class="mt-2">
                            <span class="d-block text-subtitle-1 font-weight-medium">
                                {{ $t('description_type.short') }}
                            </span>
                            <span v-html="info.descriptions.description.find(el => el.type === 'short').description" />
                        </div>
                        <div v-if="info.descriptions.description.find(el => el.type === 'general')" class="mt-2">
                            <span class="d-block text-subtitle-1 font-weight-medium">
                                {{ $t('description_type.general') }}
                            </span>
                            <span
                                v-html="info.descriptions.description.find(el => el.type === 'general').description"
                            />
                        </div>
                        <div v-if="info.descriptions.description.find(el => el.type === 'pleasenote')" class="mt-2">
                            <span class="d-block text-subtitle-1 font-weight-medium">
                                {{ $t('important_information') }}
                            </span>
                            <span
                                v-html="info.descriptions.description.find(el => el.type === 'pleasenote').description"
                            />
                        </div>
                    </v-col>
                    <v-col :cols="12" md="8" class="mt-3">
                        <div class="hidden-lg-and-down">
                            <v-row no-gutters>
                                <v-col class="col-title">
                                    <v-icon start size="small">mdi-star-circle-outline</v-icon>
                                    {{ $t('services') }}:
                                </v-col>
                                <v-col class="pl-2 d-flex flex-wrap">
                                    <v-chip
                                        v-for="service in info.services.service"
                                        :key="service.supplierSpecificCode"
                                        size="small"
                                        class="px-2 mr-1 mt-1"
                                    >
                                        {{ service.otaname || service.supplierServiceName }}
                                    </v-chip>
                                </v-col>
                            </v-row>
                        </div>
                    </v-col>
                    <hotel-amenities :info="info" />
                </template>
            </service-info-dialog>
            <client-only>
                <v-dialog v-if="info && info.latitude && info.longitude" v-model="mapModal" :max-width="500">
                    <map-location
                        v-if="mapModal"
                        :latitude="info.latitude"
                        :longitude="info.longitude"
                        :height="`500px`"
                    />
                </v-dialog>
            </client-only>
        </template>
    </service-card>
</template>

<script lang="ts">
    import {Component, toNative} from 'vue-facing-decorator'
    import __AccommodationService, {
        AccommodationService as _AccommodationService,
    } from '@/components/account/orderServices/AccommodationService.vue'
    import RoomSustainabilityNote from '~~/customization_layers/zsh/app/components/account/orderServices/snippets/RoomSustainabilityNote.vue'

    // UI-24-1: render the sustainability room note (Zero: added amenities; Sustainable:
    // omitted amenities) with an info (i) icon opening the included-amenities modal.
    // AccommodationService is consumed via the accommodationService.src.js indirection, so it
    // is overridden through the mod .src.js+Mod.vue alias pattern (like OrderInfoBaseMod),
    // NOT via customization_layers auto-import. Mixin-extends the core class (inherits all
    // props/getters/methods) and provides a custom template where the former ZERO
    // amenity-pills block is replaced by <RoomSustainabilityNote>.
    @Component({components: {RoomSustainabilityNote}})
    export class AccommodationService extends _AccommodationService {}

    export default {...toNative(AccommodationService)}
</script>
